import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input } from 'rsuite';
import {
  DEFAULT_PEEK_COUNT,
  movePromptsForCommandCard,
  moveTargetsFor,
  peekCountError,
  RESHUFFLE_SOURCE_ORDER,
  reshuffleToggleVisible,
  swapWithNeighbour,
  toggleReshuffleZone,
  viewedIds,
  ZONE_LABELS,
} from '../../../shared/playtest';
import CardTile from './CardTile';
import ReplacementPrompt from './ReplacementPrompt';
import { displayOf, refusalText } from './playtestDisplay';
import { useDragSourceProps } from './PlaytestDnD';
import {
  SWEPT_TO_COMMAND,
  SWEPT_TO_LIBRARY,
  SWEPT_TOGGLE_LABEL,
  sweptToCommand,
} from './playtestReshuffle';
import { usePlaytestSessions } from './PlaytestSessionProvider';
import { FRAME_BORDER, FRAME_RADIUS } from './playtestStyles';

const SPACING = { section: 12, row: 8 };

const REORDER_UP = -1;
const REORDER_DOWN = 1;

const CLOSED_PEEK = { open: false, input: '', count: DEFAULT_PEEK_COUNT, error: null };

const PEEK_ERROR_TEXT = {
  'not-a-whole-number': 'Enter a whole number.',
  'below-one': 'Enter at least one card.',
  'above-library': 'That is more than the cards left in the library.',
};

const styles = {
  panel: { display: 'flex', flexDirection: 'column', gap: SPACING.section },
  row: { display: 'flex', flexWrap: 'wrap', gap: SPACING.row, alignItems: 'center' },
  column: { display: 'flex', flexDirection: 'column', gap: SPACING.row },
  sheet: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.row,
    padding: SPACING.section,
    border: FRAME_BORDER,
    borderRadius: FRAME_RADIUS,
  },
};

/**
 * Library dialog content: View top N (reorder the peeked cards) and the
 * shuffle actions. The sheet picks which zones shuffle into the library.
 */
const PlaytestLibraryPanel = ({ collectionId, session, onOpenDetail, dragHandlers = {} }) => {
  const { dispatchAction, dispatchActions } = usePlaytestSessions();
  const dragSourceProps = useDragSourceProps();
  const state = session.engine;
  const [peek, setPeek] = useState(CLOSED_PEEK);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetZones, setSheetZones] = useState([]);
  const [sheetToCommand, setSheetToCommand] = useState(false);
  const [sheetError, setSheetError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [moveOpenId, setMoveOpenId] = useState(null);
  const [movePending, setMovePending] = useState(null);

  // A refused action shows its reason. The message clears on the next accepted action.
  const dispatch = (action) => {
    const result = dispatchAction(collectionId, action);
    setActionMessage(result === 'ok' ? null : refusalText(result));
    return result;
  };
  const viewed = peek.open ? viewedIds(state, peek.count) : [];
  const sheetToggleVisible = reshuffleToggleVisible(state, sheetZones);

  const openPeek = () =>
    setPeek({ open: true, input: String(DEFAULT_PEEK_COUNT), count: DEFAULT_PEEK_COUNT, error: null });

  const applyPeekInput = () => {
    const error = peekCountError(peek.input, state.zones.library.length);
    if (error) {
      setPeek((prev) => ({ ...prev, error }));
      return;
    }
    setPeek((prev) => ({ ...prev, count: Number(prev.input.trim()), error: null }));
  };

  const reorderView = (index, offset) => {
    const next = swapWithNeighbour(viewed, index, offset);
    if (next === viewed) return;
    dispatch({ type: 'reorderTop', instanceIds: next });
  };

  const closeMoveMenu = () => {
    setMoveOpenId(null);
    setMovePending(null);
  };

  const openMove = (instanceId) => {
    setMoveOpenId(instanceId);
    setMovePending(null);
  };

  // The menu closes only when the engine accepts the move. A refusal stays open and says why.
  const moveViewed = (instanceId, to) => {
    if (dispatch({ type: 'move', instanceId, to }) === 'ok') closeMoveMenu();
  };

  // A command-zone card that leaves command asks where it goes first, as the detail panel does.
  const chooseMove = (instanceId, target) => {
    if (movePromptsForCommandCard(state, instanceId, target.to)) {
      setMovePending({ instanceId, target });
      return;
    }
    moveViewed(instanceId, target.to);
  };

  const closePeek = () => {
    setPeek(CLOSED_PEEK);
    closeMoveMenu();
  };

  const openSheet = () => {
    setSheetZones(state.reshuffleFrom);
    setSheetToCommand(sweptToCommand(state.sweptCommandCardsTo));
    setSheetError(null);
    setSheetOpen(true);
  };

  const confirmSheet = () => {
    // The zone list and the swept-command toggle apply as one step: one undo entry, and neither
    // takes effect when the reshuffle is refused.
    const actions = [];
    if (sheetToggleVisible) {
      const to = sheetToCommand ? SWEPT_TO_COMMAND : SWEPT_TO_LIBRARY;
      if (to !== state.sweptCommandCardsTo) actions.push({ type: 'setSweptCommandDestination', to });
    }
    actions.push({ type: 'reshuffle', from: sheetZones });
    const result = dispatchActions(collectionId, actions);
    if (result === 'ok') {
      setSheetError(null);
      setSheetOpen(false);
      return;
    }
    setSheetError(refusalText(result));
  };

  const renderMoveControl = (instanceId, name) => {
    if (movePending !== null && movePending.instanceId === instanceId) {
      return (
        <ReplacementPrompt
          name={name}
          destinationLabel={movePending.target.label}
          onDestination={() => moveViewed(instanceId, movePending.target.to)}
          onCommand={() => moveViewed(instanceId, 'command')}
          onCancel={() => setMovePending(null)}
        />
      );
    }
    if (moveOpenId !== instanceId) return null;
    return (
      <div data-testid="viewed-move-menu" style={styles.row}>
        {moveTargetsFor(state, instanceId).map((target) => (
          <Button
            key={target.to}
            size="xs"
            data-testid={`viewed-move-target-${target.to}`}
            onClick={() => chooseMove(instanceId, target)}
          >
            {target.label}
          </Button>
        ))}
      </div>
    );
  };

  const renderViewed = () =>
    viewed.map((instanceId, index) => {
      const view = displayOf(session, state.instances[instanceId].cardId);
      return (
        <div key={instanceId} style={styles.column}>
          <CardTile
            testId={`viewed-card-${instanceId}`}
            name={view.name}
            typeLine={view.typeLine}
            imageSmall={view.imageSmall}
            dragProps={dragSourceProps(instanceId, dragHandlers)}
            onClick={() => onOpenDetail(instanceId)}
          />
          <div style={styles.row}>
            <Button
              size="xs"
              data-testid={`viewed-up-${instanceId}`}
              disabled={index === 0}
              onClick={() => reorderView(index, REORDER_UP)}
            >
              Up
            </Button>
            <Button
              size="xs"
              data-testid={`viewed-down-${instanceId}`}
              disabled={index === viewed.length - 1}
              onClick={() => reorderView(index, REORDER_DOWN)}
            >
              Down
            </Button>
            <Button
              size="xs"
              data-testid={`viewed-move-${instanceId}`}
              onClick={() => openMove(instanceId)}
            >
              Move
            </Button>
          </div>
          {renderMoveControl(instanceId, view.name)}
        </div>
      );
    });

  return (
    <div data-testid="playtest-library-panel" style={styles.panel}>
      <div style={styles.row}>
        <Button size="sm" data-testid="view-top-open" onClick={openPeek}>
          View top N
        </Button>
        <Button data-testid="shuffle-library" onClick={() => dispatch({ type: 'reshuffle', from: [] })}>
          Shuffle library
        </Button>
        <Button data-testid="shuffle-into-library" onClick={openSheet}>
          Shuffle into library
        </Button>
      </div>
      {actionMessage ? <span data-testid="library-action-error">{actionMessage}</span> : null}

      {peek.open ? (
        <div data-testid="view-top-panel" style={styles.panel}>
          <div style={styles.row}>
            <Input
              data-testid="view-top-input"
              value={peek.input}
              onChange={(value) => setPeek((prev) => ({ ...prev, input: value }))}
            />
            <Button size="sm" data-testid="view-top-apply" onClick={applyPeekInput}>
              Apply
            </Button>
            <Button size="sm" data-testid="view-top-close" onClick={closePeek}>
              Close
            </Button>
          </div>
          {peek.error ? <span data-testid="view-top-error">{PEEK_ERROR_TEXT[peek.error]}</span> : null}
          <div style={styles.row}>{renderViewed()}</div>
        </div>
      ) : null}

      {sheetOpen ? (
        <div data-testid="shuffle-sheet" style={styles.sheet}>
          <span>Shuffle these zones into the library:</span>
          {RESHUFFLE_SOURCE_ORDER.map((zone) => (
            <label key={zone}>
              <input
                type="checkbox"
                data-testid={`shuffle-zone-${zone}`}
                checked={sheetZones.includes(zone)}
                onChange={() => setSheetZones(toggleReshuffleZone(sheetZones, zone))}
              />
              {` ${ZONE_LABELS[zone]}`}
            </label>
          ))}
          {sheetToggleVisible ? (
            <label style={styles.row}>
              <input
                type="checkbox"
                data-testid="shuffle-sheet-command-toggle"
                checked={sheetToCommand}
                onChange={(event) => setSheetToCommand(event.target.checked)}
              />
              {SWEPT_TOGGLE_LABEL}
            </label>
          ) : null}
          {sheetError ? <span data-testid="shuffle-sheet-error">{sheetError}</span> : null}
          <div style={styles.row}>
            <Button data-testid="shuffle-confirm" onClick={confirmSheet}>
              Confirm
            </Button>
            <Button data-testid="shuffle-cancel" onClick={() => setSheetOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

PlaytestLibraryPanel.propTypes = {
  collectionId: PropTypes.string.isRequired,
  session: PropTypes.shape({
    engine: PropTypes.object.isRequired,
    cardViews: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  dragHandlers: PropTypes.shape({ onStart: PropTypes.func, onEnd: PropTypes.func }),
};

export default PlaytestLibraryPanel;
